// src/screens/superadmin/products/Products.jsx

import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";
import {
  FaSearch,
  FaBox,
  FaCheckCircle,
  FaClock,
  FaBan,
  FaTimes,
} from "react-icons/fa";
import { Row, Col } from "react-bootstrap";
import Pagination from "../../../components/general/pagination/Pagination";
import ActionDropdown from "../../../components/admin/dropdown/action/ActionDropdown";
import StatCard from "../../../components/admin/statcard/StatCard";
import AdminLayout from "../../../layouts/AdminLayout";
import MyContext from "../../../context/MyContext";
import "../../../assets/css/admin/products/Products.css";
import { ProductsListSkeleton } from "../../../components/general/skeleton/SkeletonBlock";

const ProductsList = () => {
  const navigate = useNavigate();
  const { baseUrl, baseUrlForFiles, authToken } = useContext(MyContext);

  const [productsList, setProductsList] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [openActionMenu, setOpenActionMenu] = useState(null);
  const [filterStatus, setFilterStatus] = useState("all");
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(true);

  const [totalProducts, setTotalProducts] = useState(0);
  const [activeProducts, setActiveProducts] = useState(0);
  const [inactiveProducts, setInactiveProducts] = useState(0);
  const [outOfStockProducts, setOutOfStockProducts] = useState(0);

  // 1. Options bhi string banao
  const statusOptions = [
    { value: "all", label: "All Status" },
    { value: "active", label: "Active" },
    { value: "inactive", label: "Inactive" },
  ];

  const viewAllProducts = () => {
    setLoading(true);
    axios
      .get(`${baseUrl}/admin/product/viewAllProducts`, {
        headers: { Authorization: `Bearer ${authToken}` },
      })
      .then((response) => {
        setLoading(false);
        if (response.data.error) {
          setErrorMessage(response.data.error);
          Swal.fire({
            icon: "error",
            title: "Error",
            text: response.data.error,
          });
        } else {
          setProductsList(response.data.product);
        }
      })
      .catch((error) => {
        setLoading(false);
        console.log(error);
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Products will not load.",
        });
      });
  };

  const changeProductStatus = (product) => {
    const formData = new FormData();
    formData.append("id", product.id);

    axios
      .post(`${baseUrl}/admin/product/changeProductStatus`, formData, {
        headers: { Authorization: `Bearer ${authToken}` },
      })
      .then((response) => {
        if (response.data.success == true) {
          Swal.fire({
            icon: "success",
            title: "Success",
            text: "Product status successfully updated!",
          });
          viewAllProducts();
        }
      })
      .catch((error) => {
        console.log(error);
      });
  };

  useEffect(() => {
    if (!authToken) {
      setLoading(false);
      navigate("/admin/login");
      return;
    }
    viewAllProducts();
  }, [authToken]);

  // 2. Stats calculation me string use karo
  useEffect(() => {
    setTotalProducts(productsList.length);
    setActiveProducts(productsList.filter((p) => p.status === "active").length);
    setInactiveProducts(
      productsList.filter((p) => p.status === "inactive").length,
    );
    setOutOfStockProducts(
      productsList.filter((p) => p.stock === 0 || p.stock === "0").length,
    );
  }, [productsList]);

  // 3. Filter logic me string use karo
  const filteredProducts = productsList.filter((p) => {
    const matchSearch = p.name
      ?.toLowerCase()
      .includes(searchTerm.toLowerCase());
    const matchStatus = filterStatus === "all" || p.status === filterStatus;
    return matchSearch && matchStatus;
  });

  // 4. Actions aur display me string use karo
  const getActions = (product) => [
    {
      icon: "MdVisibility",
      label: "View Details",
      onClick: () => {
        setSelectedProduct(product);
        setShowViewModal(true);
        setOpenActionMenu(null);
      },
    },
    {
      icon: product.status === "active" ? "MdToggleOff" : "MdToggleOn",
      label: product.status === "active" ? "Inactive" : "Activate",
      onClick: () => {
        changeProductStatus(product);
        setOpenActionMenu(null);
      },
      variant: product.status === "active" ? "warning" : "success",
    },
  ];

  const statsData = [
    {
      icon: FaBox,
      iconBg: "linear-gradient(135deg,#e3f2fd,#bbdefb)",
      iconColor: "#2196f3",
      label: "Total Products",
      value: totalProducts.toString(),
      badge: "All",
    },
    {
      icon: FaCheckCircle,
      iconBg: "linear-gradient(135deg,#e8f5f0,#d4ede3)",
      iconColor: "#0ac46c",
      label: "Active Products",
      value: activeProducts.toString(),
      badge: "Live",
    },
    {
      icon: FaClock,
      iconBg: "linear-gradient(135deg,#fff3e0,#ffe0b2)",
      iconColor: "#ff9800",
      label: "Inactive Products",
      value: inactiveProducts.toString(),
      badge: "Inactive",
    },
    {
      icon: FaBan,
      iconBg: "linear-gradient(135deg,#ffebee,#ffcdd2)",
      iconColor: "#f44336",
      label: "Out of Stock",
      value: outOfStockProducts.toString(),
      badge: "Alert",
    },
  ];

  if (loading) {
    return (
      <AdminLayout>
        <ProductsListSkeleton />
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="product-inventory-container">
        <div className="product-inventory-header">
          <div>
            <h1>Products Management</h1>
            <p>Tam sellers ke products dekhen aur status manage karein</p>
          </div>
        </div>

        <Row className="g-4 mb-4">
          {statsData.map((stat, idx) => (
            <Col key={idx} xs={12} sm={6} xl={3}>
              <StatCard {...stat} />
            </Col>
          ))}
        </Row>

        <div className="product-table-section">
          <div className="product-table-header">
            <select
              className="ksa-product-filter-select"
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
            >
              {statusOptions.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>

            <div className="product-search">
              <FaSearch size={14} />
              <input
                type="text"
                placeholder="Search products..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>

          <div className="product-table-wrapper">
            <table className="product-table">
              <thead>
                <tr>
                  <th>IMAGE</th>
                  <th>PRODUCT NAME</th>
                  <th>CATEGORY</th>
                  <th>SUB CATEGORY</th>
                  <th>SELLER</th>
                  <th>UNIT</th>
                  <th>PRICE</th>
                  <th>STOCK</th>
                  <th>STATUS</th>
                  <th>ACTIONS</th>
                </tr>
              </thead>
              <tbody>
                {filteredProducts
                  .slice(
                    (currentPage - 1) * itemsPerPage,
                    currentPage * itemsPerPage,
                  )
                  .map((product) => (
                    <tr key={product.id}>
                      <td>
                        <img
                          src={`${baseUrlForFiles}/${product.image}`}
                          alt={product.name}
                          className="product-image"
                        />
                      </td>
                      <td className="product-name">{product.name}</td>
                      <td>{product.kmsubcategory?.kmcategory?.categoryname}</td>
                      <td>{product.kmsubcategory?.subcategoryname}</td>
                      <td>{product.kmseller?.shop_name}</td>
                      <td>
                        <span className="product-unit-badge">
                          {product.unit}
                        </span>
                      </td>
                      <td className="product-price">
                        PKR {product.price?.toLocaleString()}
                      </td>
                      <td
                        className={
                          product.stock == 0 || product.stock === "0"
                            ? "ksa-product-stock-zero"
                            : ""
                        }
                      >
                        {product.stock}
                      </td>
                      <td>
                        <span
                          className={`product-status ${
                            product.status === "active"
                              ? "in-stock"
                              : "out-stock"
                          }`}
                        >
                          {product.status === "active" ? "Active" : "Inactive"}
                        </span>
                      </td>
                      <td>
                        <ActionDropdown
                          actions={getActions(product)}
                        />
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>

          <Pagination
            totalItems={filteredProducts.length}
            itemsPerPage={itemsPerPage}
            currentPage={currentPage}
            onPageChange={setCurrentPage}
            onItemsPerPageChange={(n) => {
              setItemsPerPage(n);
              setCurrentPage(1);
            }}
          />
        </div>

        {showViewModal && selectedProduct && (
          <div
            className="ksa-modal-overlay"
            onClick={() => setShowViewModal(false)}
          >
            <div
              className="ksa-modal ksa-modal-lg ksa-category-modal"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="ksa-category-modal-header">
                <h3 className="ksa-category-modal-title">Product Details</h3>
                <button
                  className="ksa-modal-close"
                  onClick={() => setShowViewModal(false)}
                >
                  <FaTimes size={18} />
                </button>
              </div>

              <div className="ksa-category-modal-body">
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "16px",
                    paddingBottom: "18px",
                    marginBottom: "6px",
                    borderBottom: "1px solid #e5e7eb",
                  }}
                >
                  <img
                    src={`${baseUrlForFiles}/${selectedProduct.image}`}
                    alt={selectedProduct.name}
                    style={{
                      width: "72px",
                      height: "72px",
                      borderRadius: "10px",
                      objectFit: "cover",
                      flexShrink: 0,
                      border: "1px solid #e5e7eb",
                    }}
                  />
                  <div style={{ minWidth: 0 }}>
                    <h3
                      style={{
                        margin: 0,
                        fontSize: "18px",
                        fontWeight: 600,
                        color: "#111827",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {selectedProduct.name}
                    </h3>
                    <span
                      className={`product-status ${
                        selectedProduct.status === "active"
                          ? "in-stock"
                          : "out-stock"
                      }`}
                      style={{ marginTop: "6px", display: "inline-block" }}
                    >
                      {selectedProduct.status === "active"
                        ? "Active"
                        : "Inactive"}
                    </span>
                  </div>
                </div>

                {[
                  ["Category", selectedProduct.kmsubcategory?.kmcategory?.categoryname || "N/A"],
                  ["Sub category", selectedProduct.kmsubcategory?.subcategoryname || "N/A"],
                  ["Seller", selectedProduct.kmseller?.shop_name || "N/A"],
                  ["Unit", selectedProduct.unit || "N/A"],
                  ["Price", selectedProduct.price != null ? `PKR ${selectedProduct.price.toLocaleString()}` : "N/A"],
                  ["Stock", selectedProduct.stock ?? "N/A"],
                ].map(([label, value]) => (
                  <div
                    key={label}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: "11px 0",
                      borderBottom: "1px solid #f1f2f4",
                    }}
                  >
                    <span style={{ fontSize: "14px", color: "#6b7280" }}>
                      {label}
                    </span>
                    <span
                      style={{
                        fontSize: "14px",
                        fontWeight: 500,
                        color: "#111827",
                        textAlign: "right",
                      }}
                    >
                      {value}
                    </span>
                  </div>
                ))}

                {selectedProduct.description && (
                  <div style={{ paddingTop: "14px" }}>
                    <span
                      style={{
                        fontSize: "14px",
                        color: "#6b7280",
                        display: "block",
                        marginBottom: "4px",
                      }}
                    >
                      Description
                    </span>
                    <p
                      style={{
                        margin: 0,
                        fontSize: "14px",
                        color: "#374151",
                        lineHeight: 1.6,
                      }}
                    >
                      {selectedProduct.description}
                    </p>
                  </div>
                )}
              </div>

              <div className="ksa-category-modal-footer">
                <button
                  className="outline-gradient"
                  onClick={() => setShowViewModal(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default ProductsList;
